import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import Table from "components/Table";
import useConsent from "hooks/useConsent";
import usePaginationHook from "hooks/usePagination";
import { useSnackbar } from "hooks/useSnackbar";
import React from "react";
import { getEOSList, initiateReqEOS } from "services/eosRewardConfigServices";
import sessionHelperInstance from "services/sessionHelper";
import { sortArray } from "utils/miscUtils";
import { PermittedRoles } from "utils/permissions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// Utility function to check if retry should be shown
const shouldShowRetry = (data: any) => {
  const hasPermission = sessionHelperInstance.hasRole(
    PermittedRoles.EOS_REWARD_CONFIG.CREATE_ROLES
  );

  if (!data?.status || !hasPermission) return false;
  if (data.status === "COMPLETED") return false;
  if (!data.startAt) return false;

  if (data.status === "STARTED") {
    // if startAt is older than 30mins and status is started then show the button
    const startAtDate = new Date(data.startAt);
    const now = new Date();
    const diffMs = now.getTime() - startAtDate.getTime();
    const diffMins = diffMs / (1000 * 60);

    return diffMins >= 30;
  }

  return true;
};

const RowActions = ({ data, actionHandlers: { handleConsent = (id?: any) => {} } }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  return shouldShowRetry(data) ? (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleConsent(data?._id)}>Retry</MenuItem>
      </Menu>
    </>
  ) : (
    "-"
  );
};

const InitiateEOS = () => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(true);
  const [listData, setListData] = React.useState([]);
  const [columns, setColumns] = React.useState([]);
  const [orderDirection, setOrderDirection] = React.useState("asc");
  const hasPermission = sessionHelperInstance.hasRole(
    PermittedRoles.EOS_REWARD_CONFIG.CREATE_ROLES
  );
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const _openConsent = async (resetId = null, cid = 1) => {
    updateDetails({
      cid: cid,
      itemName: "initiate EOS",
      id: resetId,
    });

    openConsent();
  };

  const reopenConsent = async (resetId, cid = 2) => {
    closeConsent();
    await new Promise((resolve) => setTimeout(resolve, 1000));
    updateDetails({
      cid: cid,
      id: resetId,
    });
    _openConsent(resetId, cid);
  };

  const initiateEOS = async (resetId) => {
    try {
      closeConsent();
      await initiateReqEOS(resetId);
    } catch (error) {
      console.log(error);
      showSnackbar("Failed to initiate EOS. Please try again.", "error");
    } finally {
      showSnackbar(
        "EOS initiated successfully. Please wait for the process to complete.",
        "success"
      );
    }
  };

  React.useEffect(() => {
    const cols = [
      {
        label: "ID",
        id: "_id",
      },
      {
        label: "Status",
        id: "status",
      },
      {
        label: "Start At",
        id: "startAt",
      },
      {
        label: "End At",
        id: "endAt",
      },
      {
        label: "Initiated By",
        id: "initiatedByEmail",
      },
      {
        label: "ERROR occured",
        id: "error",
        showMore: true, // this is important to expand/collaps large error string if any
      },
    ];

    hasPermission &&
      cols.push({
        label: "Actions",
        id: "actions",
      });

    setColumns(cols);

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getEOSList();
        const data = response.data.map((item) => {
          const errorString = JSON.stringify(item.error);
          return {
            ...item,
            error: item?.error ? errorString : "-",
          };
        });
        setListData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pageNo, pageSize, hasPermission]);

  return (
    <ContentShell
      title="Initiate EOS & EOS LIST"
      loading={loading}
      actions={
        hasPermission ? (
          <CustomButton
            startIcon={<RestartAltIcon />}
            onClick={() => _openConsent()}
          >
            Initiate End of Season Reset
          </CustomButton>
        ) : null
      }
    >
      <Box>
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          actions={RowActions}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{ handleConsent: _openConsent }}
        />
      </Box>

      <DeleteConsentDialog
        btnText={
          constentDetails.cid === 1 ? "Next" : "Initiate End of Season Reset!"
        }
        message={
          constentDetails.cid === 1
            ? "Are you sure you want to "
            : "This operation will delete all user data. Are you sure you want to proceed?"
        }
        itemName={constentDetails.itemName}
        open={isOpenConsent}
        onClose={closeConsent}
        onConfirm={() =>
          constentDetails?.cid === 2
            ? initiateEOS(constentDetails?.id)
            : reopenConsent(constentDetails?.id, 2)
        }
      />
    </ContentShell>
  );
};

export default InitiateEOS;
